import React from "react";

import type { OptionType } from "@impero-com/common/components/form/fields/types";
import {
    GridDirection,
    GridDisplay,
} from "@impero-com/common/components/form/legacyFields/gridDisplay";
import { ERRORED_CLASSNAME } from "@impero-com/common/utils/form/erroredElements";
import { assertNever } from "@impero-com/common/utils/obj";
import { FormBox, FormField, FormLabel } from "@impero-com/ui";
import Radio from "antd/es/radio";
import { cx } from "class-variance-authority";

import * as theme from "./theme.css";

export type RadioButtonGroupInputValueType = string | number | boolean | null;
export interface RadioButtonGroupInputBaseProps<T> {
    options: OptionType<T>[];
    label?: React.ReactNode;
    disabled?: boolean;
    buttonStyle?: "outline" | "solid"; // Defaults to "solid"
    type?: "gridRadio" | "button"; // Determines the rendering style defaults to "gridRadio"
    // Number of columns, defaults to 1
    columns?: number;
    direction?: GridDirection;
    allowDeselect?: boolean;
}

export interface RadioButtonGroupInputProps<
    T,
> extends RadioButtonGroupInputBaseProps<T> {
    value: T | null;
    error?: string;
    onChange?: (value: T) => void;
    onBlur?: () => void;
    fieldRef?: React.RefCallback<HTMLElement>;
    // allowDeselect: if true, the user can uncheck the radio button by clicking on it again
    // This is non-standard behavior we hesitate to change due it being adopted from the start
    allowDeselect?: boolean;
}

/**
 * Basic form component based on ant design's Radio.Group
 */
export const RadioButtonGroupInput = <
    T extends RadioButtonGroupInputValueType,
>({
    value,
    error,
    onChange,
    onBlur,
    fieldRef,
    label,
    options,
    direction,
    allowDeselect = false,
    disabled = false,
    buttonStyle = "solid",
    type = "gridRadio",
    columns = 1,
}: RadioButtonGroupInputProps<T>) => {
    const handleChange = (changeValue: T) => {
        if (!onChange) {
            return;
        }
        const shouldUncheck = changeValue === value && allowDeselect;
        if (shouldUncheck) {
            onChange(null as T);
        } else {
            onChange(changeValue);
        }
        onBlur?.(); // May be required for FA input compatibility
    };

    function renderChildren(): React.ReactNode {
        switch (type) {
            case "gridRadio":
                return (
                    <GridDisplay
                        columns={columns}
                        items={options}
                        direction={direction}
                        renderItem={(option) => (
                            <Radio
                                key={option.value.toString()}
                                value={option.value}
                                disabled={option.disabled || disabled}
                                onClick={() => handleChange?.(option.value)}
                            >
                                {option.label}
                            </Radio>
                        )}
                    />
                );

            case "button":
                return options.map((option) => (
                    <Radio.Button
                        key={option.value.toString()}
                        value={option.value}
                        disabled={option.disabled || disabled}
                    >
                        {option.label}
                    </Radio.Button>
                ));

            default:
                return assertNever(type);
        }
    }
    return (
        <FormBox>
            <FormField
                error={error}
                className={cx(error && ERRORED_CLASSNAME)}
                lite={label === undefined}
            >
                {label && <FormLabel disabled={disabled}>{label}</FormLabel>}
                <Radio.Group
                    ref={fieldRef}
                    value={value}
                    disabled={disabled}
                    onBlur={onBlur}
                    onChange={(e) => handleChange(e.target.value as T)}
                    buttonStyle={buttonStyle}
                    className={cx(!onChange && theme.nonInteractive)}
                >
                    {renderChildren()}
                </Radio.Group>
            </FormField>
        </FormBox>
    );
};
